import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';

import { Api } from '../../../../../../../shared/constants/api';
import { DigitalPropertyService } from '../../../../../services/digital-property.service';
import { IValidateBulkUpload, IValidationErrors } from '../../../../../../../shared/interfaces/digital-property.interface';
import { BusMessageChannels, BusMessageService } from '../../../../../services/bus-message.service';
import { NotificationPosition } from '../../../../../models/notification.model';
import { UserService } from '../../../../../services/user.service';
import { NotificationService } from '../../../../../services/notification.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { ErrorMessageService } from '../../../../../services/error-message.service';
import { A11yService } from '../../../../../services/a11y.service';
import { IGlobalError } from '../../../../../../../shared/interfaces/error.interface';
import { DigitalPropertyWorkspaceFilterComponent } from '../../../../../components/digital-property-workspace-filter/digital-property-workspace-filter.component';
import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableRow,
} from '../../../../../components/table/ngb-table/utilities/ngb-table.interface';
import { NgbTableUtilities } from '../../../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { MimeTypes } from '../../../../../../../shared/constants/mime-type';
import { LoadErrorHandler } from '../../../../../services/load-error-handler';
import { SharedDateUtility } from '../../../../../../../shared/utils/date.utility';
import { ICellConfig } from '../../../../../components/table/ngb-table/cells/base-cell/base-cell.component';
import { bulkUpload } from '../../../../../../../shared/constants/bulk-workbook';
import { CommonUtility } from '../../../../../utility/common.utility';

@Component({
  selector: 'app-add-multiple-digital-property',
  templateUrl: './add-multiple-digital-property.component.html',
  styleUrls: ['./add-multiple-digital-property.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AddMultipleDigitalPropertyComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription;
  private bulkUploadFile: File | undefined;

  @Input() public homeLink: string[];
  @Input() public modalMode: boolean = false;
  @Input() public set selectedWorkspaceId(value: string) {
    if (SharedCommonUtility.notNullishOrEmpty(value) && this.modalMode) {
      this.workspaceId = value;
    }
  }
  @Output() public cancelled: EventEmitter<void> = new EventEmitter<void>();
  @Output() public save: EventEmitter<void> = new EventEmitter<void>();

  public ACCEPTED_FILE_TYPES: string[] = [MimeTypes.XLSX];
  public FILE_UPLOADED_MESSAGE: string = 'label_digital_property_will_be_added';
  public ACCEPTED_FILE_ERROR: string = 'error_only_xlsx_file_allowed_for_upload';
  public FILE_DATA_ERROR: string = 'label_invalid_dp_missing_information_details';
  public BULK_UPLOAD_DIGITAL_PROPERTY_TEMPLATE_NAME: string = 'Bulk DA creation template.xlsx';

  public workspaceId: string;
  public Api: typeof Api;
  public saveError: IGlobalError;
  public formBulk: UntypedFormGroup;
  public fileValid: boolean;
  public fileName: string;
  public fileUploaded: boolean;
  public fileValidationResult: IValidateBulkUpload;
  public fileDropError: string;

  public tableConfig: ITableConfig;
  public tableData: ITableRow[];

  constructor(
    private activatedRoute: ActivatedRoute,
    private digitalPropertyService: DigitalPropertyService,
    private userService: UserService,
    private busMessageService: BusMessageService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
    private errorMessageService: ErrorMessageService,
    private loadErrorHandler: LoadErrorHandler,
    private a11yService: A11yService,
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    this.subscriptions = new Subscription();
    this.Api = Api;
    this.buildForm();
    this.bulkUploadFile = undefined;
    this.fileValid = undefined;
    this.fileUploaded = undefined;
    this.fileDropError = undefined;
  }

  private getTableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      Issue: {
        translationKey: 'issues',
        styles: { minWidth: '80%', width: '80%' },
      },
      Row: {
        translationKey: 'column_header_row',
        styles: { minWidth: '20%', width: '20%' },
      },
    };

    return {
      columns: columns,
      caption: this.translateService.instant('label_correct_fields_below'),
      singleSelect: {
        selectable: false,
        selectFirstRow: true,
      },
    };
  }

  private getTableData(result: IValidateBulkUpload): ITableRow[] {
    const toTableRow = (error: IValidationErrors): ITableRow => {
      const data: Record<string, IDclConfig<ICellConfig>> = {
        Issue: NgbTableUtilities.textCell({
          text: this.translateService.instant(error.message),
        }),
        Row: NgbTableUtilities.textCell({
          text: error.lines.join(', '),
        }),
      };
      return { data };
    };
    return result.validateErrors.map(toTableRow);
  }

  private handleError(message: string, response: HttpErrorResponse): void {
    const errorMessage: string = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage: string = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);
    this.loadErrorHandler.handleErrorBindable(
      `${appMessage}: ${errorMessage}`,
      AddMultipleDigitalPropertyComponent.name,
      response,
    );
    console.error(`[${AddMultipleDigitalPropertyComponent.name}.handleError: `, appMessage, ']', response, errorMessage);

    this.saveError = response.error;

    this.changeDetectorRef.detectChanges();
  }

  private onValidateDigitalPropertyBulkUploadSuccess(result: IValidateBulkUpload): void {
    this.fileValid = result.validate;
    this.fileValidationResult = result;
    this.fileUploaded = true;
    if (!this.fileValid) {
      this.tableConfig = this.getTableConfig();
      this.tableData = this.getTableData(result);
    }
    this.changeDetectorRef.detectChanges();
  }

  private onValidateDigitalPropertyBulkUploadError(response: HttpErrorResponse): void {
    this.handleError('error_validation_failed', response);
  }

  private onDownloadDigitalPropertyTemplateError(response: HttpErrorResponse): void {
    this.handleError('error_file_missing', response);
  }

  private onCreateBulkDigitalPropertySuccess(result: string): void {
    this.userService.reloadUser();
    this.busMessageService.to(BusMessageChannels.digitalPropertyChange).next();

    this.notificationService.success(
      this.translateService.instant('create_digital_property_bulk_success', result),
      NotificationPosition.Toast,
      true,
    );

    const action: string = this.modalMode ? 'save' : null;
    this.emitActionOrNavigate(action);
  }

  private onCreateBulkDigitalPropertyError(response: HttpErrorResponse): void {
    this.handleError('create_digital_property_bulk_error', response);
  }

  private emitActionOrNavigate(action: string): void {
    switch (action) {
      case 'save':
        this.save.emit();
        break;
      case 'cancel':
        this.cancelled.emit();
        break;
      default:
        this.router.navigate(this.homeLink).then();
        break;
    }
  }

  public onSave(): void {
    const action: string = this.modalMode ? 'save' : null;
    this.emitActionOrNavigate(action);
  }

  public onCancel(): void {
    const action: string = this.modalMode ? 'cancel' : null;
    this.emitActionOrNavigate(action);
  }

  public createBulkDigitalProperty(): void {
    if (SharedCommonUtility.isNullish(this.bulkUploadFile)) {
      return;
    }

    const bulkUploadFileForm: FormData = new FormData();
    bulkUploadFileForm.append(bulkUpload, this.bulkUploadFile, this.bulkUploadFile.name);
    bulkUploadFileForm.append('monitoringDayStartTimestamp', SharedDateUtility.toMidnight(new Date()).getTime().toString());

    this.subscriptions.add(
      this.digitalPropertyService.createBulkDigitalProperty(this.workspaceId, bulkUploadFileForm).subscribe({
        next: this.onCreateBulkDigitalPropertySuccess.bind(this),
        error: this.onCreateBulkDigitalPropertyError.bind(this),
      }),
    );
  }

  public buildForm(): void {
    this.formBulk = this.formBuilder.group({
      [bulkUpload]: [null, []],
    });
    this.fileValid = true;
  }

  public inputFile(file?: File): void {
    if (file) {
      this.bulkUploadFile = file;
      this.fileName = this.bulkUploadFile.name;
      this.fileUploaded = false;
    } else {
      this.bulkUploadFile = undefined;
      this.fileValid = undefined;
      this.fileName = undefined;
      this.fileUploaded = undefined;
    }
  }

  public validate(): void {
    const bulkUploadFileForm: FormData = new FormData();
    bulkUploadFileForm.append(bulkUpload, this.bulkUploadFile, this.bulkUploadFile.name);

    this.subscriptions.add(
      this.digitalPropertyService.validateDigitalPropertyBulkUpload(this.workspaceId, bulkUploadFileForm).subscribe({
        next: this.onValidateDigitalPropertyBulkUploadSuccess.bind(this),
        error: this.onValidateDigitalPropertyBulkUploadError.bind(this),
      }),
    );
  }

  public downloadTemplate(): void {
    // For modal context, use window.open to avoid modal interference
    if (this.modalMode) {
      const url: string = this.digitalPropertyService.getDownloadTemplateUrl(this.workspaceId);
      CommonUtility.navigateToExternalUrl(url, true);
    } else {
      this.subscriptions.add(
        this.digitalPropertyService.downloadDigitalPropertyTemplate(this.workspaceId).subscribe({
          error: this.onDownloadDigitalPropertyTemplateError.bind(this),
        }),
      );
    }
  }

  public ngOnInit(): void {
    if (!this.modalMode) {
      this.subscriptions.add(
        this.activatedRoute.paramMap.subscribe((params: ParamMap) => {
          this.workspaceId = params.get(Api.workspaceId);
        }),
      );
    }

    this.subscriptions.add(
      this.busMessageService
        .from(BusMessageChannels.userSwitchedToDigitalProperty)
        .pipe(skip(1))
        .subscribe((): any => {
          this.router.navigate(['/', Api.dashboard]);
        }),
    );
  }

  public ngOnDestroy(): void {
    DigitalPropertyWorkspaceFilterComponent.setOnChangeConfirmation(this);
    this.subscriptions.unsubscribe();
  }
}
